import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";

const Section = ({ id, number, title, children, delay = 0 }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            id={id}
            ref={ref}
            className={`scroll-mt-24 transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <div className="flex items-center gap-4 mb-5">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 font-black text-sm shrink-0">
                    {number}
                </span>
                <h2 className="text-2xl font-bold text-white">{title}</h2>
            </div>
            <div className="pl-14">{children}</div>
        </section>
    );
};

const TocItem = ({ href, number, title, active }) => (
    <a
        href={href}
        className={`group flex items-center gap-3 py-2 text-sm transition-all duration-200 ${active ? "text-amber-400 font-semibold" : "text-slate-500 hover:text-slate-300"}`}
    >
        <span className={`w-6 h-6 flex items-center justify-center rounded-md text-[10px] font-bold transition-colors ${active ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-500 group-hover:bg-slate-700"}`}>
            {number}
        </span>
        <span className="line-clamp-1">{title}</span>
    </a>
);

const InfoCard = ({ icon, title, items }) => (
    <div className="p-5 rounded-xl bg-slate-950/50 border border-slate-800/50">
        <div className="flex items-center gap-2 mb-4">
            <i className={`bi ${icon} text-amber-500`} aria-hidden="true" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h3>
        </div>
        <ul className="space-y-3">
            {items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500/60 mt-1.5 shrink-0" />
                    {item}
                </li>
            ))}
        </ul>
    </div>
);

export default function Privacy() {
    const [activeSection, setActiveSection] = useState("intro");
    const [progress, setProgress] = useState(0);

    const sections = [
        { id: "intro", number: "1", title: "Introduction" },
        { id: "collect", number: "2", title: "Information We Collect" },
        { id: "process", number: "3", title: "How We Process Data" },
        { id: "security", number: "4", title: "Data Storage & Security" },
        { id: "deletion", number: "5", title: "Data Deletion" },
        { id: "contact", number: "6", title: "Get in Touch" },
    ];

    useEffect(() => {
        const onScroll = () => {
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            setProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);

            for (let i = sections.length - 1; i >= 0; i--) {
                const el = document.getElementById(sections[i].id);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    if (rect.top <= 150) {
                        setActiveSection(sections[i].id);
                        break;
                    }
                }
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="bg-slate-950 min-h-screen text-slate-300 relative overflow-hidden">
            {/* Ambient background */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-500/5 blur-[150px] rounded-full" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/5 blur-[120px] rounded-full" />
            </div>

            {/* Scroll Progress */}
            <div className="fixed top-0 left-0 w-full h-[3px] z-50 bg-transparent">
                <div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-150 ease-out"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="max-w-7xl mx-auto px-6 py-24 relative z-10">
                <div className="grid lg:grid-cols-12 gap-12">

                    {/* Sidebar Navigation */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-28 space-y-8">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-widest text-amber-500 mb-4 block">
                                    Table of Contents
                                </span>
                                <nav className="space-y-1 border-l-2 border-slate-800 pl-4">
                                    {sections.map((s) => (
                                        <TocItem
                                            key={s.id}
                                            href={`#${s.id}`}
                                            number={s.number}
                                            title={s.title}
                                            active={activeSection === s.id}
                                        />
                                    ))}
                                </nav>
                            </div>

                            <div className="p-5 rounded-2xl bg-slate-900/80 border border-slate-800">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                                        <i className="bi bi-shield-check" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <p className="text-white font-bold text-sm">Questions?</p>
                                        <p className="text-xs text-slate-500">We're here to help</p>
                                    </div>
                                </div>
                                <a
                                    href="mailto:compliance@delightgalaxy.com"
                                    className="block w-full text-center py-2.5 rounded-xl bg-amber-500 text-slate-950 font-bold text-sm hover:bg-amber-400 transition-colors"
                                >
                                    Email Compliance
                                </a>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="lg:col-span-9">
                        <div className="bg-slate-900/60 backdrop-blur-sm rounded-3xl border border-slate-800 shadow-2xl overflow-hidden">

                            {/* Header */}
                            <div className="relative p-8 md:p-12 pb-8 border-b border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800/50">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs font-semibold mb-6">
                                    <i className="bi bi-shield-lock" aria-hidden="true" />
                                    Legal Policy
                                </div>

                                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white font-serif leading-tight">
                                    Privacy <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Policy</span>
                                </h1>

                                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-500">
                                    <span className="flex items-center gap-2">
                                        <i className="bi bi-calendar3" aria-hidden="true" />
                                        Last Updated: July 14, 2026
                                    </span>
                                    <span className="w-1 h-1 rounded-full bg-slate-700 hidden sm:block" />
                                    <span className="flex items-center gap-2">
                                        <i className="bi bi-building" aria-hidden="true" />
                                        Milestone and Delight Galaxy Limited
                                    </span>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="p-8 md:p-12 space-y-12 leading-relaxed">

                                <Section id="intro" number="1" title="Introduction">
                                    <p className="text-slate-400 leading-relaxed">
                                        Milestone and Delight Galaxy Limited ("we", "our", "us") values your privacy.
                                        This privacy policy describes how we collect, process, protect, and handle your
                                        information when you interact with our websites, software services, API integrations,
                                        and enterprise IT consulting offerings.
                                    </p>
                                </Section>

                                <Section id="collect" number="2" title="Information We Collect" delay={100}>
                                    <p className="text-slate-400 mb-5">
                                        We collect personal and technical information to deliver secure software solutions,
                                        enterprise hardware setups, and ERP services:
                                    </p>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <InfoCard
                                            icon="bi bi-person-vcard"
                                            title="Client Details"
                                            items={[
                                                "Representative name & corporate email",
                                                "Phone & company address",
                                                "Tax identification numbers",
                                            ]}
                                        />
                                        <InfoCard
                                            icon="bi bi-key"
                                            title="Integration Credentials"
                                            items={[
                                                "Secure API tokens & database keys",
                                                "SAP B1 configuration access",
                                                "URA EFRIS portal credentials",
                                            ]}
                                        />
                                        <InfoCard
                                            icon="bi bi-activity"
                                            title="System Logs"
                                            items={[
                                                "IP addresses & browser specs",
                                                "API request timestamps",
                                                "Performance metrics & errors",
                                            ]}
                                        />
                                    </div>
                                </Section>

                                <Section id="process" number="3" title="How We Process Data" delay={100}>
                                    <p className="text-slate-400 mb-5">
                                        Your information is processed to ensure system interoperability and custom development execution:
                                    </p>
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        {[
                                            "Providing managed IT support and hardware diagnostics",
                                            "Executing secure integrations, including EFRIS invoice mapping",
                                            "Authoring and deploying custom features inside SAP B1",
                                            "Guaranteeing system uptime, error logging, and encryption protocols",
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-slate-950/50 border border-slate-800/50 text-sm text-slate-300 list-none">
                                                <i className="bi bi-check-circle-fill text-amber-500/80 mt-0.5 shrink-0" aria-hidden="true" />
                                                {item}
                                            </li>
                                        ))}
                                    </div>
                                </Section>

                                <Section id="security" number="4" title="Data Storage & Security" delay={100}>
                                    <div className="p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/10 flex items-start gap-4">
                                        <i className="bi bi-shield-lock text-emerald-400 text-xl mt-1" aria-hidden="true" />
                                        <p className="text-slate-400 text-sm leading-relaxed">
                                            We employ industry-leading database encryption, tokenized authentications, and secure
                                            firewalls to protect all client credentials and system logs. Your business records are
                                            stored on designated secure servers or within authorized client-managed cloud hubs,
                                            in full compliance with local tax authority storage standards and digital data regulations.
                                        </p>
                                    </div>
                                </Section>

                                <Section id="deletion" number="5" title="Data Deletion" delay={100}>
                                    <p className="text-slate-400 mb-5">
                                        Client accounts and associated configurations can be permanently purged or archived
                                        upon operational contract closure.
                                    </p>
                                    <div className="flex items-start gap-4 p-5 rounded-xl bg-slate-950 border border-slate-800">
                                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
                                            <i className="bi bi-trash3" aria-hidden="true" />
                                        </div>
                                        <div>
                                            <p className="text-white font-semibold text-sm mb-1">Request Data Removal</p>
                                            <p className="text-slate-400 text-sm mb-3">
                                                To file a formal request to destroy account records, view our dedicated policy.
                                            </p>
                                            <Link
                                                to="/delete-policy"
                                                className="inline-flex items-center gap-2 text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors"
                                            >
                                                View Data Deletion Policy
                                                <i className="bi bi-arrow-right" aria-hidden="true" />
                                            </Link>
                                        </div>
                                    </div>
                                </Section>

                                <Section id="contact" number="6" title="Get in Touch" delay={100}>
                                    <p className="text-slate-400 mb-6">
                                        If you have concerns about our software data processing or security measures,
                                        please contact our compliance department:
                                    </p>

                                    <div className="flex flex-col sm:flex-row items-start gap-6 p-6 rounded-2xl bg-slate-950 border border-slate-800">
                                        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 shrink-0 overflow-hidden">
                                            <img
                                                src={logo}
                                                alt="Delight Galaxy"
                                                className="w-10 h-10 object-contain"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<span class="text-white font-black text-xl">D</span>';
                                                }}
                                            />
                                        </div>

                                        <div className="space-y-3 text-sm">
                                            <p className="text-white font-bold text-lg">Milestone and Delight Galaxy Limited</p>

                                            <a href="mailto:compliance@delightgalaxy.com" className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors">
                                                <i className="bi bi-envelope text-amber-500" aria-hidden="true" />
                                                compliance@delightgalaxy.com
                                            </a>

                                            <a href="https://milestoneanddelight.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors">
                                                <i className="bi bi-globe text-amber-500" aria-hidden="true" />
                                                milestoneanddelight.com
                                            </a>

                                            <p className="flex items-center gap-2 text-slate-400">
                                                <i className="bi bi-geo-alt text-amber-500" aria-hidden="true" />
                                                Kampala, Uganda
                                            </p>
                                        </div>
                                    </div>
                                </Section>

                            </div>

                            {/* Footer of card */}
                            <div className="px-8 md:px-12 py-6 border-t border-slate-800 bg-slate-950/30 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-600">
                                <p>© {new Date().getFullYear()} Milestone and Delight Galaxy Limited. All rights reserved.</p>
                                <div className="flex items-center gap-2">
                                    <i className="bi bi-shield-check text-emerald-500/80" aria-hidden="true" />
                                    <span>GDPR & POPIA Aligned Privacy Practices</span>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}